import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaStickyNote } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import useFundNotes from '../../../../hooks/useFundNotes';
import NotesTable from '../../utilities/NotesPage/NotesTable/NotesTable.component';
import { Note } from '../../../../sharedInterfaces/notes';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { zIndexes } from '../../../../styling/zIndexes';
import { mainColors } from '../../../../styling/theme';
export type NoteType = 'basic' | 'table';
export interface NotesProps {
  fundId: string;
  fundName: string;
  subject?: string;
  topic?: string;
  positionDate: string;
  noteType?: NoteType;
  otherUrlProvided?: string;
  shouldCall?: boolean; // Optional override - this allows use to call all notes and render UI if notes exist
  includeSection?: boolean; // Optional - this allows us to include the section in the note data
  preFetchedNotes?: Note[]; // Optional pre-fetched notes to avoid making individual API calls
  preFetchedNotesLoading?: boolean; // Optional loading state for pre-fetched notes
}

// the NoteData is the transformed note
// we receive it in its pure form from the server,
// then we transform it to look like this for consumption
export interface NoteData {
  positionDate: string;
  date: string;
  noteValue: string;
  noteType: NoteType;
  noteSection?: string; // this is the section of the note, for example "table" or "basic"
  subject?: string; // alias for noteSection for table column field
  /** Display name of the user who created the note (when provided by API). */
  userName?: string;
  /** Poster id from API (`fund_note_user_id`). */
  fund_note_user_id?: string;
}

const NotesForm = React.memo(
  ({
    handler,
    fundName,
  }: {
    fundName: string;
    handler: (note: string) => void;
  }) => {
    const [input, setInput] = useState<string>('');

    const submit = (e: React.FormEvent) => {
      e.preventDefault();
      if (input.trim()) {
        handler(input.trim());
        setInput('');
      }
    };

    return (
      <div className="w-full border-b border-slate-200 bg-slate-50 px-4 py-3">
        <form onSubmit={submit} className="w-full">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your note here..."
              className="flex-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 transition-colors placeholder:text-gray-400 focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue"
            />
            <button
              type="submit"
              disabled={!input.trim()}
              className="whitespace-nowrap rounded-lg bg-mainblue px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-mainblue/90 focus:outline-none focus:ring-2 focus:ring-mainblue focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-mainblue"
            >
              Add Note
            </button>
          </div>
        </form>
      </div>
    );
  },
);

// Here is some old school event listerns that are required to trigger react re-renders
// The reason we are not using context is to keep react's re-rendering from blowing up when there are many notes components in a tree - He we trigger single useeffects within the children with a global variable and a set of listeners
type Listener = () => void;

const listeners = new Set<Listener>();

export let OPEN_POPOVER_ID: string | null = null;

export const subscribeToPopoverChanges = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

export const notifyPopoverChange = () => {
  for (const cb of listeners) {
    cb();
  }
};

export default function NotesComp({
  fundId,
  subject,
  topic,
  fundName,
  positionDate,
  otherUrlProvided,
  includeSection = false,
  preFetchedNotes,
  preFetchedNotesLoading = false,
}: NotesProps): React.ReactElement | null {
  const id = `${fundId}-${positionDate}-${subject || ''}-${topic || ''}`;
  const isOpen = OPEN_POPOVER_ID === id;

  const [, setForceUpdate] = useState(0); //REQUIRED DUMMY RE-RENDER

  useEffect(() => {
    const unsubscribe = subscribeToPopoverChanges(() => {
      setForceUpdate((v) => v + 1);
    });

    return unsubscribe;
  }, []);

  // Floating UI for popover positioning - anchor to the side
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!open && OPEN_POPOVER_ID === id) {
        OPEN_POPOVER_ID = null;
        notifyPopoverChange();
      }
    },
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['left-start', 'right-end', 'left-end'],
      }),
      shift({ padding: 8 }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  // Always use subject/topic for filtering when fetching notes
  // includeSection only controls whether to show the "Section" column in the UI, not filtering behavior
  // Individual pages should filter by their specific subject, overview pages use preFetchedNotes (all notes)
  const subjectKey = subject; // Always filter by subject if provided
  const topicKey = topic; // Always filter by topic if provided

  // Use pre-fetched notes if provided, otherwise fetch individually
  // We still enable the query so that refetch works when notes are posted
  // Always pass original subject/topic for posting, even if we use subjectKey/topicKey for fetching
  // Only skip the query if preFetchedNotes is provided (even if empty array)
  // Empty array means "we pre-fetched and there are no notes", undefined means "fetch individually"
  const shouldCallQuery = preFetchedNotes === undefined;

  const {
    notes: fetchedNotes,
    fetchFundNotes,
    postFundNotes,
    loading: isLoadingNotes,
  } = useFundNotes({
    fundName: fundId,
    subject: subjectKey, // Used for filtering when fetching
    topic: topicKey, // Used for filtering when fetching
    postSubject: subject, // Always use original subject for posting (required by backend)
    postTopic: topic, // Always use original topic for posting
    positionDate: positionDate, // positionDate is required, no fallback
    shouldCall: shouldCallQuery, // Only auto-fetch if preFetchedNotes is not provided
    otherUrlProvided,
  });

  // Use pre-fetched notes if available (even if empty), otherwise use fetched notes
  // Pre-fetched notes take priority to avoid unnecessary individual API calls
  // Empty array from pre-fetch means "no notes", undefined means "fetch individually"
  // Note: When using preFetchedNotes, optimistic updates will still work via the cache
  // because the mutation updates the 'fund-notes-multiple' query cache
  const notes = preFetchedNotes !== undefined ? preFetchedNotes : fetchedNotes;

  // Show loading when: we should call the query and we're loading with no notes yet
  const isLoading =
    (shouldCallQuery && isLoadingNotes && notes.length === 0) ||
    (!shouldCallQuery && preFetchedNotesLoading && notes.length === 0);

  const hasNotes = notes.length > 0;

  const tableData: NoteData[] = useMemo(
    () =>
      notes.map((note) => ({
        positionDate: note.fund_note_position_date,
        date: note.fund_note_timestamp,
        noteValue: note.fund_note_text,
        noteSection: note.fund_note_subject,
        subject: note.fund_note_subject, // alias for table column field
        userName: note.user_name,
        fund_note_user_id: note.fund_note_user_id,
        noteType: note.fund_note_text.includes('TABLE_NOTE:')
          ? 'table'
          : 'basic',
      })),
    [notes],
  );

  const handleSubmit = async (noteString: string) => {
    // The mutation handles optimistic updates via the cache
    // No need to manually manage optimistic state
    await postFundNotes(noteString);
  };

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`group relative flex aspect-square h-8 w-8 cursor-pointer items-center justify-center rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${isOpen
          ? 'border-mainblue bg-mainblue text-white shadow-lg ring-2 ring-mainblue ring-offset-1'
          : 'border-gray-300 bg-white text-mainblue shadow-sm hover:border-mainblue hover:bg-mainblue hover:text-white hover:shadow-md'
          }`}
        onClick={(e) => {
          e.stopPropagation();
          // LOGIC TO TOGGLE THE POPOVER
          if (OPEN_POPOVER_ID === id) {
            OPEN_POPOVER_ID = null;
          } else {
            OPEN_POPOVER_ID = id;
          }
          notifyPopoverChange();
        }}
        aria-label="Notes"
      >
        <FaStickyNote className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
        {isLoading && !isOpen ? (
          <span className="absolute -right-1 -top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-gray-400 shadow-lg ring-2 ring-white">
            <div className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
          </span>
        ) : hasNotes ? (
          <span className="absolute -right-1 -top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-lg ring-2 ring-white">
            {notes.length > 99 ? '99+' : notes.length}
          </span>
        ) : null}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: zIndexes.notesPopover }}
            {...getFloatingProps()}
            className="flex max-h-[70vh] w-[760px] flex-col overflow-hidden rounded-lg border border-mainblue bg-slate-50 shadow-xl"
          >
            {/* Header with close button */}
            <div
              className="flex shrink-0 items-center justify-between border-b px-4 py-2.5"
              style={{
                backgroundColor: mainColors.mainBlue,
                borderColor: mainColors.mainBlue,
              }}
            >
              <div className="flex items-center gap-1.5">
                <div className="flex h-6 w-6 items-center justify-center rounded bg-white/15">
                  <FaStickyNote className="h-3.5 w-3.5 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-white">Notes</h2>
              </div>
              <button
                onClick={() => {
                  OPEN_POPOVER_ID = null;
                  notifyPopoverChange();
                }}
                className="flex h-6 w-6 items-center justify-center rounded text-white/80 transition-colors hover:bg-white/15 hover:text-white focus:outline-none focus:ring-1 focus:ring-white/60"
                aria-label="Close notes"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {/* Form section */}
            <div className="shrink-0">
              <NotesForm handler={handleSubmit} fundName={fundName} />
            </div>

            {/* Notes table section - scrollable */}
            <div
              className="min-h-0 flex-1 overflow-y-auto bg-slate-50"
              style={{ maxHeight: '50vh' }}
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center px-4 py-12">
                  <div className="mb-3 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-mainblue"></div>
                  <p className="text-xs font-medium text-gray-600">
                    Loading notes...
                  </p>
                </div>
              ) : tableData.length > 0 ? (
                <div className="px-4 py-3">
                  <NotesTable
                    tableData={tableData}
                    showToolbar={true}
                    title={fundName}
                    loadingHeight="2rem"
                    includeSection={includeSection}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center px-4 py-8">
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                    <FaStickyNote className="h-5 w-5 text-gray-400" />
                  </div>
                  <p className="text-xs font-medium text-gray-900">
                    No notes yet
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Add your first note above
                  </p>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
